// frontend/modules/service-metrics/views/ServiceMetricsDashboardView.tsx
import React, { useState } from 'react';
import { 
    Card, 
    Typography, 
    Button, 
    Space, 
    Select, 
    Row, 
    Col, 
    Tabs,
    Divider,
    Breadcrumb
} from 'antd';
import { 
    ReloadOutlined, 
    DashboardOutlined,
    UnorderedListOutlined,
    WarningOutlined,
    ArrowLeftOutlined,
    DatabaseOutlined,
    ApiOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useServiceMetrics } from '../hooks/useServiceMetrics';
import ServiceHealthCard from '../components/ServiceHealthCard';
import ServiceMetricsTrendChart from '../components/ServiceMetricsTrendChart';
import TopServicesTable from '../components/TopServicesTable';
import ServiceLogsTable from '../components/ServiceLogsTable';
import RecentErrorsCard from '../components/RecentErrorsCard';
import ServiceSummaryStats from '../components/ServiceSummaryStats';
import HikariPoolDashboard from '../components/HikariPoolDashboard';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const ServiceMetricsDashboardView: React.FC = () => {
    const { dark } = useTheme();
    const {
        selectedServiceSummary,
        serviceHealth,
        serviceLogs,
        topServices,
        servicesWithFailures,
        isLoading,
        selectedService,
        filters,
        currentPage,
        pageSize,
        totalElements,
        selectService,
        refreshData,
        setFilters,
        setSelectedService,
        fetchServiceLogs,
    } = useServiceMetrics();

    const [activeTab, setActiveTab] = useState('overview');

    const handleServiceSelect = (serviceName: string) => {
        selectService(serviceName);
        setActiveTab('details');
    };

    const handleBackToDashboard = () => {
        setSelectedService(null);
        setActiveTab('overview');
    };

    const handleDaysChange = (days: number) => {
        setFilters({ days });
    };

    const handlePageChange = (page: number, size: number) => {
        if (selectedService) {
            fetchServiceLogs(selectedService, page, size);
        }
    };

    return (
        <div className="space-y-6">
            <Card className={dark ? 'bg-gray-800 text-white' : ''}>
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <Breadcrumb>
                            <Breadcrumb.Item>
                                <a onClick={handleBackToDashboard}>
                                    <DashboardOutlined /> Service Metrics
                                </a>
                            </Breadcrumb.Item>
                            {selectedService && (
                                <Breadcrumb.Item>{selectedService}</Breadcrumb.Item>
                            )}
                        </Breadcrumb>
                        <Title level={4} className={dark ? 'text-white mt-2' : 'mt-2'}>
                            {selectedService ? `${selectedService} Details` : 'Service Metrics Dashboard'}
                        </Title>
                    </div>
                    <Space>
                        <Select
                            value={filters.days}
                            onChange={handleDaysChange}
                            style={{ width: 120 }}
                        >
                            <Option value={1}>Last 24h</Option>
                            <Option value={7}>Last 7 days</Option>
                            <Option value={14}>Last 14 days</Option>
                            <Option value={30}>Last 30 days</Option>
                        </Select>
                        {selectedService && (
                            <Button 
                                icon={<ArrowLeftOutlined />} 
                                onClick={handleBackToDashboard}
                            >
                                Back
                            </Button>
                        )}
                        <Button 
                            icon={<ReloadOutlined />} 
                            onClick={refreshData}
                            loading={isLoading}
                        >
                            Refresh
                        </Button>
                    </Space>
                </div>

                {!selectedService ? (
                    // Dashboard Overview
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <TabPane 
                            tab={<span><DashboardOutlined /> Overview</span>} 
                            key="overview"
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <TopServicesTable
                                        data={topServices}
                                        loading={isLoading}
                                        title="Top Services by Request Volume"
                                        onServiceClick={handleServiceSelect}
                                    />
                                </Col>
                            </Row>
                        </TabPane>
                        
                        <TabPane 
                            tab={<span><WarningOutlined /> Services with Failures</span>} 
                            key="failures"
                        >
                            <TopServicesTable
                                data={servicesWithFailures}
                                loading={isLoading}
                                title="Services with Failures"
                                onServiceClick={handleServiceSelect}
                            />
                        </TabPane>

                        <TabPane 
                            tab={<span><ApiOutlined /> ActionFlow Metrics</span>} 
                            key="actionflow"
                        >
                            <TopServicesTable
                                data={topServices?.filter(s => s.serviceName === 'ActionFlow') || []}
                                loading={isLoading}
                                title="ActionFlow Service Metrics"
                                onServiceClick={handleServiceSelect}
                            />
                        </TabPane>

                        <TabPane 
                            tab={<span><DatabaseOutlined /> HikariCP Pool</span>} 
                            key="hikari"
                        >
                            <HikariPoolDashboard />
                        </TabPane>
                    </Tabs>
                ) : (
                    // Service Details View
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <TabPane 
                            tab={<span><DashboardOutlined /> Summary</span>} 
                            key="details"
                        >
                            <Row gutter={[16, 16]}>
                                <Col xs={24} lg={8}>
                                    <ServiceHealthCard 
                                        health={serviceHealth} 
                                        loading={isLoading}
                                    />
                                </Col>
                                <Col xs={24} lg={16}>
                                    <ServiceSummaryStats 
                                        summary={selectedServiceSummary} 
                                        loading={isLoading}
                                    />
                                </Col>
                            </Row>
                            
                            <Divider />
                            
                            <Row gutter={[16, 16]}>
                                <Col xs={24} lg={16}>
                                    <ServiceMetricsTrendChart
                                        data={selectedServiceSummary?.trendData || []}
                                        loading={isLoading}
                                        title="Request Trend"
                                    />
                                </Col>
                                <Col xs={24} lg={8}>
                                    <RecentErrorsCard
                                        errors={selectedServiceSummary?.recentErrors || []}
                                        loading={isLoading}
                                    />
                                </Col>
                            </Row>
                        </TabPane>
                        
                        <TabPane 
                            tab={<span><UnorderedListOutlined /> Logs</span>} 
                            key="logs"
                        >
                            <ServiceLogsTable
                                data={serviceLogs}
                                loading={isLoading}
                                title={`${selectedService} Request Logs`}
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: totalElements,
                                    onChange: handlePageChange,
                                }}
                            />
                        </TabPane>
                    </Tabs>
                )}
            </Card>
        </div>
    );
};

export default ServiceMetricsDashboardView;
